import { Text } from "@imminently/react/ui/core/text";
import { Box, Grid, InputBase } from "@mui/material";
import { get } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { Path } from "react-hook-form";
import { StylesConfig } from "react-select";
import Select from "react-select/async";

import { PropsOf } from "@emotion/react";
import { PatientEncounterChangeListener } from "@/pages/patient/patient";
import { PatientEncounter } from "@/resources/types";

export interface PatientDetailProps {
  name: string;
  valuePath?: Path<PatientEncounter>;
  valueSetPath?: Path<PatientEncounter>;
  labelPath?: Path<PatientEncounter>;
  readOnly?: boolean;
  encounter: PatientEncounter;
  onEncounterChange: PatientEncounterChangeListener;
  selectProps?: PropsOf<typeof Select>;
}

const selectStyles: StylesConfig = {
  control: (provided) => ({
    ...provided,
    borderRadius: "0.5rem",
    border: "none",
    backgroundColor: "transparent",
    minHeight: 0,
    height: "auto",
    ":hover": {
      border: "none",
      boxShadow: "none",
    },
    ":focus-within": {
      border: "none",
      boxShadow: "none",
    },
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    opacity: state.isDisabled ? 0 : undefined,
    padding: 0,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
  }),
  input: (provided) => ({
    ...provided,
    padding: 0,
    margin: 0,

    "::placeholder": {
      color: "#9a9da9",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    margin: 0,
    color: "#9a9da9",
  }),
  container: (provided) => ({
    ...provided,
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: "1.4375em",
    fontFamily: "Lato,Nunito,Roboto,Arial,sans-serif",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    margin: 0,
    color: state.isDisabled && state.hasValue ? "#333" : undefined,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 10,
    border: "1px solid #e5e5e5",
    borderRadius: "0.5rem",
    backgroundColor: "#fff",
    boxShadow: "none",
  }),
};

const PatientDetail = (props: PatientDetailProps) => {
  const {
    name,
    selectProps,
    onEncounterChange,
    encounter,
    labelPath,
    valuePath,
    valueSetPath,
    readOnly,
  } = props;

  const value = valuePath && get(encounter, valuePath);
  let label = labelPath && get(encounter, labelPath);

  if (!label && selectProps?.defaultOptions) {
    label = (selectProps.defaultOptions as any[]).find(
      (option: any) => option.value === value
    )?.label;
  }

  const placeholderValue = "Unknown";

  const handleChange = useCallback(
    (newValue: any) => {
      if (valuePath) {
        onEncounterChange(valueSetPath ?? valuePath, newValue, valuePath);
      }
    },
    [onEncounterChange, valueSetPath, valuePath]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleChange(e.target.value);
    },
    [handleChange]
  );

  const handleSelectChange = useCallback(({ value: val }: any) => {
    handleChange(val);
  }, [handleChange]);

  const [loadedOptions, setLoadedOptions] = useState<any>();
  const loadDefaultOptions = useCallback(async () => {
    if (selectProps?.loadOptions) {
      const result: any = selectProps.loadOptions("", setLoadedOptions);
      if (result?.then) {
        result.then(setLoadedOptions);
      }
    }
  }, [selectProps]);

  useEffect(() => {
    loadDefaultOptions();
  }, []);

  return (
    <Grid item xs={12} py={1}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-end"
      >
        <Text variant="h6">{name.toUpperCase()}</Text>
      </Box>
      {selectProps ? (
        <Select
          onChange={handleSelectChange}
          styles={selectStyles}
          placeholder={placeholderValue}
          defaultValue={value !== undefined && { value, label }}
          menuPosition="fixed"
          cacheOptions
          defaultOptions={loadedOptions}
          components={{ IndicatorSeparator: null }}
          isDisabled={readOnly}
          {...selectProps}
        />
      ) : (
        <InputBase
          inputProps={{ sx: { padding: 0 }, width: "100%" }}
          sx={{ width: "100%" }}
          onChange={handleInputChange}
          readOnly={readOnly}
          placeholder={placeholderValue}
          // defaultValue={value}
          value={value}
        />
      )}
    </Grid>
  );
};

export default PatientDetail;
